'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { collection, getDocs, setDoc, doc, serverTimestamp, updateDoc, deleteDoc } from '@/lib/firestoreTracked';
import { onAuthStateChanged } from 'firebase/auth';
import { Save, Trash2, Lock, Unlock, Loader2, Upload, Palette, Image as ImageIcon, Edit2, Copy, ChevronUp, ChevronDown, Crown } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import useCardDesigns from '@/hooks/useCardDesigns';
import useCustomIcons from '@/hooks/useCustomIcons';
import { auth, db } from '@/firebaseConfig';
import { FALLBACK_CARD } from '@/utils/defaultCards';
import { getIconByName, renderCustomIcon } from '@/utils/iconMap';
import PhotoshopGradientEditor from '@/components/PhotoshopGradientEditor';
import ParticleEffects from '@/components/ParticleEffects';
import { uploadToBlob, validateFile, getFilePreview } from '@/utils/blobStorage';
import { CARD_PARTICLE_PRESETS, resolveParticlePreset } from '@/utils/particlePresets';
import { DEV_UID } from '@/constants';

/**
 * Color Picker Input Component with Google-style picker
 */
function ColorPickerInput({ label, value, onChange, className = '' }) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const previewRef = React.useRef(null);
  
  const openPicker = () => {
    if (previewRef.current) {
      const rect = previewRef.current.getBoundingClientRect();
      const pickerWidth = 264;
      const pickerHeight = 300;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const maxLeft = viewportWidth - pickerWidth;
      const nextLeft = Math.max(12, Math.min(rect.left, maxLeft));
      let nextTop = rect.bottom + 8;
      if (nextTop + pickerHeight > viewportHeight - 12) {
        const topPosition = rect.top - pickerHeight - 8;
        if (topPosition > 12) {
          nextTop = topPosition;
        } else {
          nextTop = Math.max(12, (viewportHeight - pickerHeight) / 2);
        }
      }
      const clampedLeft = Math.max(12, Math.min(nextLeft, viewportWidth - pickerWidth - 12));
      setPickerPosition({
        top: nextTop,
        left: clampedLeft
      });
    }
    setShowPicker(true);
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setPortalTarget(document.body);
  }, []);
  
  return (
    <div className={`color-picker-wrapper ${className}`}>
      <label className="control-label">
        {label}
        <span className="control-value">{value}</span>
      </label>
        <div className="color-picker-input-container">
          <div 
            className="color-preview-box"
            style={{ backgroundColor: value }}
            onClick={(e) => {
              e.preventDefault();
              if (!showPicker) openPicker();
            }}
            title="Click to open color picker"
            ref={previewRef}
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g., #3b82f6"
            className="control-input color-text-input"
            onFocus={() => {
              if (!showPicker) openPicker();
            }}
            onClick={() => {
              if (!showPicker) openPicker();
            }}
          />
        </div>
      {showPicker && portalTarget && createPortal(
        <>
          <div 
            className="color-picker-backdrop"
            onClick={() => setShowPicker(false)}
          />
          <div
            className="color-picker-popover"
            style={{
              top: pickerPosition?.top ?? 0,
              left: pickerPosition?.left ?? 0
            }}
          >
            <HexColorPicker color={value} onChange={onChange} />
          </div>
        </>,
        portalTarget
      )}
      
      <style jsx>{`
        .color-picker-wrapper {
          position: relative;
          margin-bottom: 20px;
        }
        
        .color-picker-input-container {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        
        .color-preview-box {
          width: 48px;
          height: 48px;
          border: 2px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          cursor: pointer;
          flex-shrink: 0;
          transition: all 0.2s;
          touch-action: none;
        }
        
        .color-preview-box:hover {
          border-color: rgba(59, 130, 246, 0.7);
        }
        
        .color-text-input {
          flex: 1;
        }
        
        .color-picker-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2999;
        }
        
        .color-picker-popover {
          position: fixed;
          z-index: 3000;
          padding: 12px;
          background: var(--glassy-card-bg);
          backdrop-filter: blur(var(--glassy-card-blur));
          -webkit-backdrop-filter: blur(var(--glassy-card-blur));
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
        }
        
        .color-picker-popover :global(.react-colorful) {
          width: 240px;
          height: 240px;
        }

        @media (max-width: 480px) {
          .color-picker-popover :global(.react-colorful) {
            width: 200px;
            height: 200px;
          }
        }
        
        .color-picker-popover :global(.react-colorful__saturation) {
          border-radius: 5px 5px 0 0;
        }
        
        .color-picker-popover :global(.react-colorful__hue) {
          height: 20px;
          border-radius: 0 0 5px 5px;
        }
        
        .color-picker-popover :global(.react-colorful__pointer) {
          width: 24px;
          height: 24px;
        }
      `}</style>
    </div>
  );
}

/**
 * CardDesignStudio Component
 * 
 * Visual editor for creating and managing card designs and custom icons
 */
export default function CardDesignStudio() {
  const [activeTab, setActiveTab] = useState('cards'); // 'cards' or 'icons'
  
  return (
    <div className="studio-container">
      {/* Tab Navigation - Sticky header for all viewports */}
      <div className="studio-tabs">
        <button
          className={`studio-tab ${activeTab === 'cards' ? 'studio-tab--active' : ''}`}
          onClick={() => setActiveTab('cards')}
        >
          <Palette size={18} />
          Card Designs
        </button>
        <button
          className={`studio-tab ${activeTab === 'icons' ? 'studio-tab--active' : ''}`}
          onClick={() => setActiveTab('icons')}
        >
          <ImageIcon size={18} />
          Custom Icons
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'cards' ? <CardDesignsTab /> : <CustomIconsTab />}

      <style jsx>{`
        .studio-container {
          display: flex;
          flex-direction: column;
          min-height: 0;
          height: auto;
          max-height: none;
          background: transparent;
          color: #e2e8f0;
          overflow: visible;
          gap: 16px;
        }

        .studio-tabs {
          display: flex;
          gap: 8px;
          padding: 16px 0 0 0;
          border-bottom: 1px solid var(--lobby-border, rgba(147, 51, 234, 0.2));
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(6px);
          border-radius: 5px;
          padding: 12px 16px;
          flex-shrink: 0;
          position: static;
        }

        .studio-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: #94a3b8;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: -2px;
        }

        .studio-tab:hover {
          color: #e2e8f0;
          background: rgba(51, 65, 85, 0.3);
        }

        .studio-tab--active {
          color: #60a5fa;
          border-bottom-color: #3b82f6;
        }

        @media (max-width: 768px) {
          .studio-tabs {
            background: rgba(15, 23, 42, 0.6);
            padding: 10px 12px;
            gap: 6px;
            overflow-x: auto;
          }

          .studio-tab {
            padding: 10px 14px;
            font-size: 14px;
            white-space: nowrap;
          }
        }
      `}</style>
    </div>
  );
}

const BASE_CARD_CATEGORIES = [
  { id: 'basic', label: 'Basic Colours' },
  { id: 'neon', label: 'Neon Gradients' },
  { id: 'other', label: 'Other' }
];

// Card Designs Tab Component
function CardDesignsTab() {
  const { cardDesigns, loading, saveCardDesign, deleteCardDesign, toggleCardLock } = useCardDesigns({ fresh: true });
  const [isDevUser, setIsDevUser] = useState(false);
  
  const [designName, setDesignName] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#1a1a2e');
  const [borderColor, setBorderColor] = useState('#4a5568');
  const [borderWidth, setBorderWidth] = useState(1);
  const [textColor, setTextColor] = useState('#e2e8f0');
  
  // Text glow settings
  const [textGlowEnabled, setTextGlowEnabled] = useState(false);
  const [textGlowColor, setTextGlowColor] = useState('#ffffff');
  const [textGlowSize, setTextGlowSize] = useState(10);
  const [textGlowOpacity, setTextGlowOpacity] = useState(0.5);
  
  // Icon glow settings
  const [iconColor, setIconColor] = useState('#94a3b8');
  const [iconGlowEnabled, setIconGlowEnabled] = useState(false);
  const [iconGlowColor, setIconGlowColor] = useState('#3b82f6');
  const [iconGlowSize, setIconGlowSize] = useState(15);
  const [iconGlowOpacity, setIconGlowOpacity] = useState(0.6);
  
  // Card glow settings (persist unsaved changes while editing)
  const [cardOuterGlowEnabled, setCardOuterGlowEnabled] = useState(false);
  const [cardOuterGlowColor, setCardOuterGlowColor] = useState('#3b82f6');
  const [cardOuterGlowSize, setCardOuterGlowSize] = useState(20);
  const [cardOuterGlowOpacity, setCardOuterGlowOpacity] = useState(0.5);
  // Only reset outer glow if user clicks a 'Reset' button
  const resetOuterGlow = () => {
    setCardOuterGlowEnabled(false);
    setCardOuterGlowColor('#3b82f6');
    setCardOuterGlowSize(20);
    setCardOuterGlowOpacity(0.5);
  };
  
  const [cardInnerGlowEnabled, setCardInnerGlowEnabled] = useState(false);
  const [cardInnerGlowColor, setCardInnerGlowColor] = useState('#ffffff');
  const [cardInnerGlowSize, setCardInnerGlowSize] = useState(10);
  const [cardInnerGlowOpacity, setCardInnerGlowOpacity] = useState(0.3);
  
  // Background opacity and blur
  const [bgOpacity, setBgOpacity] = useState(100);
  const [bgImageOpacity, setBgImageOpacity] = useState(100);
  const [backdropBlurEnabled, setBackdropBlurEnabled] = useState(false);
  const [backdropBlurAmount, setBackdropBlurAmount] = useState(10);
  
  // Background image
  const [bgImage, setBgImage] = useState('');
  const [bgImageUploadStatus, setBgImageUploadStatus] = useState(null);
  
  // Particle effects (particles.js presets + custom JSON)
  const [particlePreset, setParticlePreset] = useState('none');
  const [particleConfigText, setParticleConfigText] = useState('');
  const [particleConfig, setParticleConfig] = useState(null);
  const [particleConfigError, setParticleConfigError] = useState(null);
  const [particleSizeScale, setParticleSizeScale] = useState(1);
  const [cardLocked, setCardLocked] = useState(false);
  const [unlockLevelInput, setUnlockLevelInput] = useState('');
  const [designCategory, setDesignCategory] = useState('basic');
  const [customCategories, setCustomCategories] = useState([]);
  
  const [previewContext] = useState('lobby');
  const [previewIcon, setPreviewIcon] = useState('Heart');
  const [previewName, setPreviewName] = useState('Player Name');
  const [saveStatus, setSaveStatus] = useState(null);
  const [selectedDesign, setSelectedDesign] = useState(null);

  const refreshCategoryCatalogSnapshot = async () => {
    try {
      await fetch('/api/profile/catalog?include=categories&refresh=1', {
        cache: 'no-store'
      });
    } catch (error) {
      console.warn('Failed to refresh card category catalog snapshot', error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'cardCategories'));
        const categories = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        if (isMounted) {
          setCustomCategories(categories);
        }
      } catch (error) {
        console.warn('Failed to load custom categories', error);
      }
    };
    fetchCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsDevUser(user?.uid === DEV_UID);
    });
    return () => unsubscribe();
  }, []);

  const isDeveloperCategoryId = (value) => String(value || '').toLowerCase() === 'developer';
  const getCategoryOrderValue = (category, fallback) => {
    const parsed = parseFloat(category?.order);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const sortCategories = (items) =>
    [...items].sort((a, b) => {
      const orderDiff = getCategoryOrderValue(a, Number.MAX_SAFE_INTEGER) - getCategoryOrderValue(b, Number.MAX_SAFE_INTEGER);
      if (orderDiff !== 0) return orderDiff;
      return (a.label || a.id || '').localeCompare(b.label || b.id || '');
    });

  const getCategoryOptions = () => {
    const base = BASE_CARD_CATEGORIES.map((category, index) => {
      const override = customCategories.find((item) => item.id === category.id);
      if (override?.deleted) return null;
      return override ? { ...category, ...override, order: getCategoryOrderValue(override, index) } : { ...category, order: index };
    }).filter(Boolean);
    const extras = customCategories
      .filter((category) => !BASE_CARD_CATEGORIES.some((baseCategory) => baseCategory.id === category.id) && !category.deleted)
      .map((category, index) => ({
        ...category,
        order: getCategoryOrderValue(category, BASE_CARD_CATEGORIES.length + index)
      }));
    return sortCategories([...base, ...extras]);
  };

  const resolveDesignCategoryId = (value) => {
    if (!value) return 'basic';
    const normalized = String(value).toLowerCase();
    const options = getCategoryOptions();
    const match = options.find((category) => category.id.toLowerCase() === normalized)
      || options.find((category) => (category.label || '').toLowerCase() === normalized);
    return match ? match.id : value;
  };

  const parseBorderWidth = (value, fallback = 1) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
  };

  const parseNumber = (value, fallback) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
  };

  const parseBoolean = (value, fallback = false) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return fallback;
    }
    if (typeof value === 'string') {
      const normalized = value.toLowerCase().trim();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
      if (normalized === '1') return true;
      if (normalized === '0') return false;
    }
    return fallback;
  };

  const normalizeColorValue = (value, fallback) => {
    if (typeof value !== 'string' || !value.trim()) return fallback;
    const trimmed = value.trim().toLowerCase();
    if (trimmed.startsWith('#')) {
      const hex = trimmed.length === 4
        ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
        : trimmed;
      if (hex.length === 7 || hex.length === 9) return hex.slice(0, 7);
      return fallback;
    }
    const match = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (!match) return fallback;
    const toHex = (num) => Math.max(0, Math.min(255, parseInt(num, 10))).toString(16).padStart(2, '0');
    return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
  };

  const handleAddCategory = async () => {
    const name = window.prompt('New category name');
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (!id) return;
    const exists = [...BASE_CARD_CATEGORIES, ...customCategories].some((category) => category.id === id);
    if (exists) return;
    const nextCategory = {
      id,
      label: trimmed,
      deleted: false,
      proOnly: false
    };
    try {
      await setDoc(doc(db, 'cardCategories', id), {
        ...nextCategory,
        createdAt: serverTimestamp()
      }, { merge: true });
      setCustomCategories((prev) => [...prev, nextCategory]);
      setDesignCategory(id);
      void refreshCategoryCatalogSnapshot();
    } catch (error) {
      console.warn('Failed to add category', error);
    }
  };

  const slugifyId = (value) => (
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );

  const getDuplicateName = (baseName) => {
    const normalizedBase = (baseName || 'Untitled').trim() || 'Untitled';
    const existingNames = new Set(cardDesigns.map((design) => String(design.name || '').toLowerCase()));
    let candidate = `${normalizedBase} Copy`;
    if (!existingNames.has(candidate.toLowerCase())) return candidate;
    let counter = 2;
    while (existingNames.has(`${normalizedBase} Copy ${counter}`.toLowerCase())) {
      counter += 1;
    }
    return `${normalizedBase} Copy ${counter}`;
  };

  const getUniqueCardId = (baseName) => {
    const baseSlug = slugifyId(baseName) || `card-${Date.now()}`;
    let candidate = baseSlug;
    let counter = 2;
    while (cardDesigns.some((design) => design.id === candidate)) {
      candidate = `${baseSlug}-${counter}`;
      counter += 1;
    }
    return candidate;
  };

  const handleRenameCategory = async (category) => {
    if (!category?.id) return;
    const name = window.prompt('Rename category', category.label || category.id);
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === category.label) return;
    try {
      await setDoc(doc(db, 'cardCategories', category.id), {
        id: category.id,
        label: trimmed,
        deleted: false,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setCustomCategories((prev) => {
        const existsIndex = prev.findIndex((item) => item.id === category.id);
        if (existsIndex >= 0) {
          const updated = [...prev];
          updated[existsIndex] = { ...updated[existsIndex], label: trimmed, deleted: false };
          return updated;
        }
        return [...prev, { id: category.id, label: trimmed, deleted: false }];
      });
      void refreshCategoryCatalogSnapshot();
    } catch (error) {
      console.warn('Failed to rename category', error);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!category?.id) return;
    if (!confirm(`Delete "${category.label || category.id}"? Cards will move to Uncategorized.`)) return;
    try {
      await setDoc(doc(db, 'cardCategories', category.id), {
        id: category.id,
        deleted: true,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setCustomCategories((prev) => {
        const existsIndex = prev.findIndex((item) => item.id === category.id);
        if (existsIndex >= 0) {
          const updated = [...prev];
          updated[existsIndex] = { ...updated[existsIndex], deleted: true };
          return updated;
        }
        return [...prev, { id: category.id, deleted: true }];
      });
      if (designCategory === category.id) {
        setDesignCategory('basic');
      }
      void refreshCategoryCatalogSnapshot();
    } catch (error) {
      console.warn('Failed to delete category', error);
    }
  };

  const persistCategoryOrder = async (category, order) => {
    if (!category?.id) return;
    await setDoc(doc(db, 'cardCategories', category.id), {
      id: category.id,
      label: category.label || category.id,
      order,
      deleted: false,
      updatedAt: serverTimestamp()
    }, { merge: true });
  };

  const handleToggleCategoryProOnly = async (category) => {
    if (!category?.id) return;
    const nextProOnly = !Boolean(category?.proOnly);
    try {
      await setDoc(doc(db, 'cardCategories', category.id), {
        id: category.id,
        label: category.label || category.id,
        proOnly: nextProOnly,
        deleted: false,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setCustomCategories((prev) => {
        const index = prev.findIndex((item) => item.id === category.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = { ...updated[index], proOnly: nextProOnly, deleted: false };
          return updated;
        }
        return [...prev, { id: category.id, label: category.label || category.id, proOnly: nextProOnly, deleted: false }];
      });
      void refreshCategoryCatalogSnapshot();
    } catch (error) {
      console.warn('Failed to toggle category Pro access', error);
    }
  };

  const handleReorderCategory = async (category, direction) => {
    const options = getCategoryOptions();
    const currentIndex = options.findIndex((item) => item.id === category.id);
    if (currentIndex < 0) return;
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= options.length) return;
    const reordered = [...options];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    setCustomCategories((prev) => {
      const next = [...prev];
      const upsert = (item, order) => {
        const index = next.findIndex((entry) => entry.id === item.id);
        const payload = { id: item.id, label: item.label || item.id, deleted: false, order };
        if (index >= 0) {
          next[index] = { ...next[index], ...payload };
        } else {
          next.push(payload);
        }
      };
      reordered.forEach((item, index) => {
        upsert(item, index + 1);
      });
      return next;
    });

    try {
      await Promise.all(
        reordered.map((item, index) => persistCategoryOrder(item, index + 1))
      );
      void refreshCategoryCatalogSnapshot();
    } catch (error) {
      console.warn('Failed to reorder categories', error);
    }
  };

  // Load selected design and fully reset state to stored values
  const handleLoadDesign = (design) => {
    const css = design?.css || {};
    setSelectedDesign(design);
    setDesignName(design?.name || '');
    setBackgroundColor(css.backgroundColor || '#1a1a2e');
    setBorderColor(css.borderColor || '#4a5568');
    setBorderWidth(parseBorderWidth(css.borderWidth, 1));
    setTextColor(normalizeColorValue(css.textColor, '#e2e8f0'));
    setIconColor(normalizeColorValue(css.iconColor, '#94a3b8'));
    const rawCategory = design?.category ?? design?.categoryId ?? design?.cardCategory ?? css.category;
    setDesignCategory(resolveDesignCategoryId(rawCategory));
    setCardLocked(Boolean(design?.locked));
    setUnlockLevelInput(
      Number.isFinite(Number(design?.unlockLevel)) && Number(design?.unlockLevel) > 0
        ? String(Number(design?.unlockLevel))
        : ''
    );

    const hasExplicitTextGlow = typeof css.textGlowEnabled === 'boolean'
      || typeof css.textGlowEnabled === 'string'
      || typeof css.textGlowSize === 'number'
      || typeof css.textGlowSize === 'string'
      || typeof css.textGlowOpacity === 'number'
      || typeof css.textGlowOpacity === 'string'
      || css.textGlowColor;
    let nextTextGlowEnabled = parseBoolean(css.textGlowEnabled, false);
    let nextTextGlowColor = normalizeColorValue(css.textGlowColor, '#ffffff');
    let nextTextGlowSize = parseNumber(css.textGlowSize, 10);
    let nextTextGlowOpacity = parseNumber(css.textGlowOpacity, 0.5);
    const textShadow = css.textShadow || 'none';
    if (!hasExplicitTextGlow && textShadow !== 'none') {
      const match = textShadow.match(/0 0 (\d+)px rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        nextTextGlowEnabled = true;
        nextTextGlowSize = parseInt(match[1], 10);
        nextTextGlowOpacity = parseFloat(match[5]);
        nextTextGlowColor = normalizeColorValue(`rgb(${match[2]}, ${match[3]}, ${match[4]})`, '#ffffff');
      }
    }
    setTextGlowEnabled(nextTextGlowEnabled);
    setTextGlowColor(nextTextGlowColor);
    setTextGlowSize(nextTextGlowSize);
    setTextGlowOpacity(nextTextGlowOpacity);

    const hasExplicitIconGlow = typeof css.iconGlowEnabled === 'boolean'
      || typeof css.iconGlowEnabled === 'string'
      || typeof css.iconGlowSize === 'number'
      || typeof css.iconGlowSize === 'string'
      || typeof css.iconGlowOpacity === 'number'
      || typeof css.iconGlowOpacity === 'string'
      || css.iconGlowColor;
    let nextIconGlowEnabled = parseBoolean(css.iconGlowEnabled, false);
    let nextIconGlowColor = normalizeColorValue(css.iconGlowColor, '#3b82f6');
    let nextIconGlowSize = parseNumber(css.iconGlowSize, 15);
    let nextIconGlowOpacity = parseNumber(css.iconGlowOpacity, 0.6);
    const iconGlow = css.iconGlow || 'none';
    if (!hasExplicitIconGlow && iconGlow !== 'none') {
      const match = iconGlow.match(/0 0 (\d+)px rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        nextIconGlowEnabled = true;
        nextIconGlowSize = parseInt(match[1], 10);
        nextIconGlowOpacity = parseFloat(match[5]);
        nextIconGlowColor = normalizeColorValue(`rgb(${match[2]}, ${match[3]}, ${match[4]})`, '#3b82f6');
      }
    }
    setIconGlowEnabled(nextIconGlowEnabled);
    setIconGlowColor(nextIconGlowColor);
    setIconGlowSize(nextIconGlowSize);
    setIconGlowOpacity(nextIconGlowOpacity);

    const hasExplicitOuterGlow = typeof css.cardOuterGlowEnabled === 'boolean'
      || typeof css.cardOuterGlowEnabled === 'string'
      || typeof css.cardOuterGlowSize === 'number'
      || typeof css.cardOuterGlowSize === 'string'
      || typeof css.cardOuterGlowOpacity === 'number'
      || typeof css.cardOuterGlowOpacity === 'string'
      || css.cardOuterGlowColor;
    const hasExplicitInnerGlow = typeof css.cardInnerGlowEnabled === 'boolean'
      || typeof css.cardInnerGlowEnabled === 'string'
      || typeof css.cardInnerGlowSize === 'number'
      || typeof css.cardInnerGlowSize === 'string'
      || typeof css.cardInnerGlowOpacity === 'number'
      || typeof css.cardInnerGlowOpacity === 'string'
      || css.cardInnerGlowColor;
    let nextOuterGlowEnabled = parseBoolean(css.cardOuterGlowEnabled, false);
    let nextOuterGlowColor = normalizeColorValue(css.cardOuterGlowColor, '#3b82f6');
    let nextOuterGlowSize = parseNumber(css.cardOuterGlowSize, 20);
    let nextOuterGlowOpacity = parseNumber(css.cardOuterGlowOpacity, 0.5);
    let nextInnerGlowEnabled = parseBoolean(css.cardInnerGlowEnabled, false);
    let nextInnerGlowColor = normalizeColorValue(css.cardInnerGlowColor, '#ffffff');
    let nextInnerGlowSize = parseNumber(css.cardInnerGlowSize, 10);
    let nextInnerGlowOpacity = parseNumber(css.cardInnerGlowOpacity, 0.3);
    const boxShadow = css.boxShadow || 'none';
    if (boxShadow !== 'none') {
      const parts = boxShadow.split(',').map(s => s.trim());
      parts.forEach((shadow) => {
        if (shadow.includes('inset')) {
          if (!hasExplicitInnerGlow) {
            const match = shadow.match(/inset 0 0 (\d+)px rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
            if (match) {
              nextInnerGlowEnabled = true;
              nextInnerGlowSize = parseInt(match[1], 10);
              nextInnerGlowOpacity = parseFloat(match[5]);
              nextInnerGlowColor = normalizeColorValue(`rgb(${match[2]}, ${match[3]}, ${match[4]})`, '#ffffff');
            }
          }
        } else if (!hasExplicitOuterGlow) {
          const match = shadow.match(/0 0 (\d+)px rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
          if (match) {
            nextOuterGlowEnabled = true;
            nextOuterGlowSize = parseInt(match[1], 10);
            nextOuterGlowOpacity = parseFloat(match[5]);
            nextOuterGlowColor = normalizeColorValue(`rgb(${match[2]}, ${match[3]}, ${match[4]})`, '#3b82f6');
          }
        }
      });
    }
    setCardOuterGlowEnabled(nextOuterGlowEnabled);
    setCardOuterGlowColor(nextOuterGlowColor);
    setCardOuterGlowSize(nextOuterGlowSize);
    setCardOuterGlowOpacity(nextOuterGlowOpacity);
    setCardInnerGlowEnabled(nextInnerGlowEnabled);
    setCardInnerGlowColor(nextInnerGlowColor);
    setCardInnerGlowSize(nextInnerGlowSize);
    setCardInnerGlowOpacity(nextInnerGlowOpacity);

    const nextBgOpacity = parseNumber(css.bgOpacity ?? css.backgroundOpacity, 1);
    setBgOpacity(Math.round(nextBgOpacity * 100));
    const storedBgImage = typeof css.bgImage === 'string'
      ? css.bgImage
      : (typeof css.backgroundImage === 'string' ? css.backgroundImage : '');
    setBgImage(storedBgImage);
    const nextBgImageOpacity = parseNumber(css.bgImageOpacity ?? css.backgroundImageOpacity, 1);
    setBgImageOpacity(Math.round(nextBgImageOpacity * 100));
    setBgImageUploadStatus(null);

    const hasExplicitBlur = typeof css.backdropBlurEnabled === 'boolean'
      || typeof css.backdropBlurEnabled === 'string'
      || typeof css.backdropBlurAmount === 'number'
      || typeof css.backdropBlurAmount === 'string';
    const blurValue = css.backdropFilter || 'none';
    let nextBackdropBlurEnabled = parseBoolean(css.backdropBlurEnabled, false);
    let nextBackdropBlurAmount = parseNumber(css.backdropBlurAmount, 10);
    if (!hasExplicitBlur) {
      if (blurValue !== 'none') {
        const blurMatch = blurValue.match(/blur\((\d+)px\)/);
        nextBackdropBlurEnabled = true;
        if (blurMatch) {
          nextBackdropBlurAmount = parseInt(blurMatch[1], 10);
        }
      }
    }
    setBackdropBlurEnabled(nextBackdropBlurEnabled);
    setBackdropBlurAmount(nextBackdropBlurAmount);

    const hasCustomConfig = Boolean(css.particleConfig);
    const resolvedPreset = hasCustomConfig
      ? 'custom'
      : resolveParticlePreset(css.particlePreset || null, css.particleEffect || null);
    setParticlePreset(resolvedPreset);
    setParticleSizeScale(parseNumber(css.particleSizeScale, 1));
    if (hasCustomConfig) {
      setParticleConfig(css.particleConfig);
      setParticleConfigText(JSON.stringify(css.particleConfig, null, 2));
      setParticleConfigError(null);
    } else {
      setParticleConfig(null);
      setParticleConfigText('');
      setParticleConfigError(null);
    }
  };

  // Helper to convert hex to rgba
  const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Generate CSS values from settings
  const getTextShadow = () => {
    if (!textGlowEnabled) return 'none';
    return `0 0 ${textGlowSize}px ${hexToRgba(textGlowColor, textGlowOpacity)}`;
  };

  const getIconGlow = () => {
    if (!iconGlowEnabled) return 'none';
    return `0 0 ${iconGlowSize}px ${hexToRgba(iconGlowColor, iconGlowOpacity)}`;
  };

  const getCardBoxShadow = () => {
    const shadows = [];
    if (cardOuterGlowEnabled) {
      shadows.push(`0 0 ${cardOuterGlowSize}px ${hexToRgba(cardOuterGlowColor, cardOuterGlowOpacity)}`);
    }
    if (cardInnerGlowEnabled) {
      shadows.push(`inset 0 0 ${cardInnerGlowSize}px ${hexToRgba(cardInnerGlowColor, cardInnerGlowOpacity)}`);
    }
    return shadows.length > 0 ? shadows.join(', ') : 'none';
  };

  const buildCardPayload = (cardId, { locked } = {}) => {
    const storedParticlePreset = particlePreset === 'custom' ? null : particlePreset;
    const storedParticleConfig = particlePreset === 'custom' ? particleConfig : null;
    const effectiveLocked = Boolean(locked ?? cardLocked);
    const parsedUnlockLevel = Number.parseInt(String(unlockLevelInput || '').trim(), 10);
    const unlockLevel = effectiveLocked && Number.isFinite(parsedUnlockLevel) && parsedUnlockLevel > 0
      ? parsedUnlockLevel
      : null;
    return {
      id: cardId,
      name: designName.trim(),
      category: resolveDesignCategoryId(designCategory),
      css: {
        backgroundColor,
        borderColor,
        borderWidth: `${borderWidth}px`,
        borderStyle: 'solid',
        textColor,
        textShadow: getTextShadow(),
        iconColor,
        iconGlow: getIconGlow(),
        boxShadow: getCardBoxShadow(),
        fontFamily: 'inherit',
        textGlowEnabled,
        textGlowColor,
        textGlowSize,
        textGlowOpacity,
        iconGlowEnabled,
        iconGlowColor,
        iconGlowSize,
        iconGlowOpacity,
        cardOuterGlowEnabled,
        cardOuterGlowColor,
        cardOuterGlowSize,
        cardOuterGlowOpacity,
        cardInnerGlowEnabled,
        cardInnerGlowColor,
        cardInnerGlowSize,
        cardInnerGlowOpacity,
        // Background properties
        bgOpacity: bgOpacity / 100,
        bgImage: bgImage || '',
        bgImageOpacity: bgImageOpacity / 100,
        backdropFilter: backdropBlurEnabled ? `blur(${backdropBlurAmount}px)` : 'none',
        backdropBlurEnabled,
        backdropBlurAmount,
        // Particle properties
        particlePreset: storedParticlePreset,
        particleConfig: storedParticleConfig,
        particleSizeScale
      },
      locked: effectiveLocked,
      unlockLevel
    };
  };

  const handleSave = async () => {
    if (!designName.trim()) {
      setSaveStatus({ type: 'error', message: 'Please enter a design name' });
      return;
    }
    if (particlePreset === 'custom' && !particleConfig) {
      setSaveStatus({ type: 'error', message: 'Custom particle JSON is invalid.' });
      return;
    }

    const cardId = selectedDesign?.id || designName.toLowerCase().replace(/\s+/g, '-');
    const cardData = buildCardPayload(cardId);
    const success = await saveCardDesign(cardId, cardData);
    
    if (success) {
      setSelectedDesign(cardData);
      setSaveStatus({ type: 'success', message: 'Design saved successfully!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } else {
      setSaveStatus({ type: 'error', message: 'Failed to save design' });
    }
  };

  const handleSaveAs = async () => {
    if (!designName.trim()) {
      setSaveStatus({ type: 'error', message: 'Please enter a design name' });
      return;
    }
    if (particlePreset === 'custom' && !particleConfig) {
      setSaveStatus({ type: 'error', message: 'Custom particle JSON is invalid.' });
      return;
    }

    let cardId = designName.toLowerCase().replace(/\s+/g, '-');
    if (cardDesigns.some((design) => design.id === cardId)) {
      cardId = `${cardId}-${Date.now()}`;
    }
    const cardData = buildCardPayload(cardId, { locked: false });
    const success = await saveCardDesign(cardId, cardData);
    
    if (success) {
      setSelectedDesign(cardData);
      setSaveStatus({ type: 'success', message: 'Design saved as new!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } else {
      setSaveStatus({ type: 'error', message: 'Failed to save design' });
    }
  };

  // Delete design
  const handleDelete = async (cardId) => {
    if (!confirm('Are you sure you want to delete this card design?')) return;
    
    const success = await deleteCardDesign(cardId);
    
    if (success) {
      setSaveStatus({ type: 'success', message: 'Design deleted successfully!' });
      if (selectedDesign?.id === cardId) {
        setSelectedDesign(null);
        setDesignName('');
      }
      setTimeout(() => setSaveStatus(null), 3000);
    } else {
      setSaveStatus({ type: 'error', message: 'Failed to delete design' });
    }
  };

  // Toggle lock
  const handleToggleLock = async (card) => {
    await toggleCardLock(card.id, card.locked);
  };

  const handleDuplicate = async (design) => {
    if (!design) return;
    const newName = getDuplicateName(design.name);
    const newId = getUniqueCardId(newName);
    const clonedDesign = {
      ...design,
      id: newId,
      name: newName,
      category: resolveDesignCategoryId(design.category),
      css: { ...(design.css || {}) },
      locked: false
    };
    const success = await saveCardDesign(newId, clonedDesign);
    if (success) {
      handleLoadDesign(clonedDesign);
      setSaveStatus({ type: 'success', message: 'Design duplicated!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } else {
      setSaveStatus({ type: 'error', message: 'Failed to duplicate design' });
    }
  };

  // Get preview styles based on context
  const getPreviewStyles = () => {
    const previewRadius = previewContext === 'leaderboard' ? '0.4rem' : '8px';
    const baseStyles = {
      color: textColor,
      textShadow: getTextShadow(),
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      position: 'relative',
      backdropFilter: backdropBlurEnabled ? `blur(${backdropBlurAmount}px)` : 'none',
      WebkitBackdropFilter: backdropBlurEnabled ? `blur(${backdropBlurAmount}px)` : 'none',
      background: 'transparent',
      border: 'none',
      '--preview-bg-opacity': bgOpacity / 100,
      '--preview-bg-image-opacity': bgImageOpacity / 100,
      '--preview-bg-image': bgImage ? `url(${bgImage})` : 'none',
      '--preview-radius': previewRadius,
      '--card-bg-opacity': bgOpacity / 100,
      '--card-bg-image-opacity': bgImageOpacity / 100,
      '--card-bg-image': bgImage ? `url(${bgImage})` : 'none',
      '--card-glow': getCardBoxShadow()
    };
    
    // Build background value with opacity and optional image
    const buildBackground = () => {
      const parts = [];
      parts.push(backgroundColor);
      return parts.join(', ');
    };

    if (previewContext === 'leaderboard') {
      // For leaderboard, handle gradient in borderLeft using border-image
      const isBorderGradient = borderColor.includes('gradient');
      
      return {
        ...baseStyles,
        '--preview-bg': buildBackground(),
        '--preview-bg-image': bgImage ? `url(${bgImage})` : 'none',
        '--preview-bg-image-opacity': bgImageOpacity / 100,
        '--preview-bg-size': bgImage ? 'cover' : 'auto',
        '--preview-bg-position': bgImage ? 'center center' : 'initial',
        '--preview-bg-repeat': bgImage ? 'no-repeat' : 'repeat',
        '--card-bg': buildBackground(),
        '--card-bg-image': bgImage ? `url(${bgImage})` : 'none',
        '--card-bg-image-opacity': bgImageOpacity / 100,
        '--card-bg-size': bgImage ? 'cover' : 'auto',
        '--card-bg-position': bgImage ? 'center center' : 'initial',
        '--card-bg-repeat': bgImage ? 'no-repeat' : 'repeat',
        // Don't apply opacity to whole card - only to background via pseudo-element
        '--card-border-width': `1px`,
        '--card-border-color': borderColor,
        '--card-border-left-width': `3px`,
        '--card-border-left-color': borderColor,
        ...(isBorderGradient ? { '--card-border-gradient': borderColor } : {}),
        paddingLeft: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingRight: '16px',
        boxShadow: 'none'
      };
    } else {
      // For lobby/finale, handle both background and border gradients
      const isBorderGradient = borderColor.includes('gradient');
      
      return {
        ...baseStyles,
        '--preview-bg': buildBackground(),
        '--preview-bg-image': bgImage ? `url(${bgImage})` : 'none',
        '--preview-bg-image-opacity': bgImageOpacity / 100,
        '--preview-bg-size': bgImage ? 'cover' : 'auto',
        '--preview-bg-position': bgImage ? 'center center' : 'initial',
        '--preview-bg-repeat': bgImage ? 'no-repeat' : 'repeat',
        '--card-bg': buildBackground(),
        '--card-bg-image': bgImage ? `url(${bgImage})` : 'none',
        '--card-bg-image-opacity': bgImageOpacity / 100,
        '--card-bg-size': bgImage ? 'cover' : 'auto',
        '--card-bg-position': bgImage ? 'center center' : 'initial',
        '--card-bg-repeat': bgImage ? 'no-repeat' : 'repeat',
        // Don't apply opacity to whole card - only to background via pseudo-element
        '--card-border-width': `1px`,
        '--card-border-color': borderColor,
        ...(isBorderGradient ? { '--card-border-gradient': borderColor } : {}),
        boxShadow: 'none',
        padding: '16px',
        minHeight: previewContext === 'finale' ? '100px' : '80px'
      };
    }
  };

  const previewStyles = getPreviewStyles();
  const iconStyles = {
    color: iconColor,
    filter: iconGlowEnabled ? `drop-shadow(${getIconGlow()})` : 'none'
  };

  const IconPreview = getIconByName(previewIcon);
  const previewIconStroke = previewContext === 'leaderboard' ? 1.5 : 2.5;
  const rawCategories = getCategoryOptions();
  const allCategories = isDevUser
    ? rawCategories
    : rawCategories.filter((category) => !isDeveloperCategoryId(category.id) && !isDeveloperCategoryId(category.label));
  const getDesignCategory = (design) => resolveDesignCategoryId(
    design?.category
    ?? design?.categoryId
    ?? design?.cardCategory
    ?? design?.css?.category
  );
  const visibleDesigns = isDevUser
    ? cardDesigns
    : cardDesigns.filter((design) => !isDeveloperCategoryId(getDesignCategory(design)));
  const isCategoryMarkedProOnly = (category) => {
    if (!category) return false;
    if (Boolean(category.proOnly)) return true;
    const label = String(category.label || '');
    const id = String(category.id || '');
    const proWordPattern = /(?:^|[^a-z])pro(?:$|[^a-z])/i;
    return proWordPattern.test(label) || proWordPattern.test(id);
  };
  const getDesignAccessLabel = (design, category = null) => {
    const isProCategory = isCategoryMarkedProOnly(category);
    if (!isProCategory) {
      return design?.locked ? 'Level Unlock' : 'Open';
    }
    return design?.locked ? 'Pro Only' : 'Free Taster';
  };
  const getDesignAccessActionTitle = (design, category = null) => {
    const isProCategory = isCategoryMarkedProOnly(category);
    if (!isProCategory) {
      return design?.locked ? 'Remove unlock requirement' : 'Require unlock';
    }
    return design?.locked ? 'Make free taster for non-Pro players' : 'Require Pro for this card';
  };
  const designsByCategory = allCategories.map((category) => ({
    ...category,
    items: visibleDesigns.filter((design) => getDesignCategory(design) === category.id)
  }));
  const uncategorizedDesigns = visibleDesigns.filter(
    (design) => !allCategories.some((category) => category.id === getDesignCategory(design))
  );
  const getDesignPreviewStyles = (design) => {
    const backgroundColorValue = design.css?.backgroundColor || FALLBACK_CARD.css.backgroundColor;
    const bgImageValue = design.css?.bgImage || design.css?.backgroundImage || '';
    const bgOpacityValue = parseNumber(design.css?.bgOpacity ?? design.css?.backgroundOpacity, 1);
    const bgImageOpacityValue = parseNumber(design.css?.bgImageOpacity ?? design.css?.backgroundImageOpacity, 1);
    const borderColorValue = design.css?.borderColor || FALLBACK_CARD.css.borderColor;
    const textColorValue = design.css?.textColor || FALLBACK_CARD.css.textColor;
    const textShadowValue = design.css?.textShadow || 'none';
    const iconColorValue = design.css?.iconColor || FALLBACK_CARD.css.iconColor;
    const iconGlowValue = design.css?.iconGlow || 'none';
    const iconGlowFilter = iconGlowValue !== 'none' ? `drop-shadow(${iconGlowValue})` : 'none';
    const boxShadowValue = design.css?.boxShadow || 'none';
    const previewStyles = {
      '--design-bg': backgroundColorValue,
      '--design-bg-opacity': bgOpacityValue,
      '--design-bg-image': bgImageValue ? `url(${bgImageValue})` : 'none',
      '--design-bg-image-opacity': bgImageOpacityValue,
      '--design-bg-size': bgImageValue ? 'cover' : 'auto',
      '--design-bg-position': bgImageValue ? 'center center' : 'initial',
      '--design-bg-repeat': bgImageValue ? 'no-repeat' : 'repeat',
      '--card-bg': backgroundColorValue,
      '--card-bg-opacity': bgOpacityValue,
      '--card-bg-image': bgImageValue ? `url(${bgImageValue})` : 'none',
      '--card-bg-image-opacity': bgImageOpacityValue,
      '--card-bg-size': bgImageValue ? 'cover' : 'auto',
      '--card-bg-position': bgImageValue ? 'center center' : 'initial',
      '--card-bg-repeat': bgImageValue ? 'no-repeat' : 'repeat',
      border: 'none',
      '--card-border-width': '1px',
      '--card-border-color': borderColorValue,
      ...(borderColorValue.includes('gradient') ? { '--card-border-gradient': borderColorValue } : {}),
      '--design-text-color': textColorValue,
      '--design-text-shadow': textShadowValue,
      '--design-icon-color': iconColorValue,
      '--design-icon-glow': iconGlowValue,
      '--design-icon-filter': iconGlowFilter,
      '--card-glow': boxShadowValue,
      boxShadow: 'none'
    };
    return previewStyles;
  };

  return (
    <>
      {saveStatus && (
        <div className={`studio-toast studio-toast--${saveStatus.type}`}>
          {saveStatus.message}
        </div>
      )}

      <div className="studio-content">
        <div className="studio-left">
          {/* Left Panel - Controls */}
          <div className="studio-preview-sticky">
            <div
              className="studio-preview-card player-card-custom"
              style={previewStyles}
              data-bg-opacity={bgOpacity / 100}
            >
              <div className="player-card-background" aria-hidden="true" />
              <div className="player-card-glow" aria-hidden="true" />
              {(particlePreset !== 'none' || particleConfig) && (
                <ParticleEffects
                  preset={particlePreset === 'custom' ? null : particlePreset}
                  config={particlePreset === 'custom' ? particleConfig : null}
                  sizeScale={particleSizeScale}
                  style={{ zIndex: 2 }}
                />
              )}
              <div className="player-card-content studio-preview-content">
                <span className="studio-preview-name">{previewName}</span>
                {IconPreview && (
                  <IconPreview size={16} strokeWidth={previewIconStroke} style={iconStyles} />
                )}
              </div>
            </div>
          </div>

          <div className="studio-panel studio-panel--controls">
          <h2 className="panel-title">Design Controls</h2>

          <div className="control-group">
            <label className="control-label">Design Name</label>
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              placeholder="Enter design name..."
              className="control-input"
            />
          </div>

          <div className="control-group">
            <label className="control-label">Category</label>
            <div className="category-buttons">
              {allCategories.map((category, index) => (
                <div key={category.id} className="category-button-group">
                  <button
                    type="button"
                    className={`category-button ${designCategory === category.id ? 'category-button--active' : ''}`}
                    onClick={() => setDesignCategory(category.id)}
                  >
                    {category.label}
                    {category.proOnly ? ' (Pro)' : ''}
                  </button>
                  <div className="category-button-actions">
                    <button
                      type="button"
                      className={`category-action ${category.proOnly ? 'category-action--active' : ''}`}
                      onClick={() => handleToggleCategoryProOnly(category)}
                      title={category.proOnly ? 'Disable Pro-only access' : 'Enable Pro-only access'}
                    >
                      <Crown size={14} />
                    </button>
                    <button
                      type="button"
                      className="category-action"
                      onClick={() => handleReorderCategory(category, -1)}
                      title="Move category up"
                      disabled={index === 0}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      className="category-action"
                      onClick={() => handleReorderCategory(category, 1)}
                      title="Move category down"
                      disabled={index === allCategories.length - 1}
                    >
                      <ChevronDown size={14} />
                    </button>
                    <button
                      type="button"
                      className="category-action"
                      onClick={() => handleRenameCategory(category)}
                      title="Rename category"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      type="button"
                      className="category-action category-action--danger"
                      onClick={() => handleDeleteCategory(category)}
                      title="Delete category"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="category-button category-button--add"
                onClick={handleAddCategory}
              >
                + Add Category
              </button>
            </div>
          </div>

          <div className="control-group">
            <label className="control-checkbox">
              <input
                type="checkbox"
                checked={cardLocked}
                onChange={(e) => setCardLocked(e.target.checked)}
              />
              <span>Gate this card behind unlock rules</span>
            </label>
            <label className="control-label" style={{ marginTop: 8 }}>
              Unlock Level (optional)
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={unlockLevelInput}
              onChange={(e) => setUnlockLevelInput(e.target.value)}
              placeholder="Blank = never auto-unlock"
              className="control-input"
              disabled={!cardLocked}
            />
            <p className="control-note">
              Locked + blank level means never unlock by level (manual unlock still works).
            </p>
          </div>

          <div className="control-section-title">Preview Settings</div>

          <div className="control-group">
            <label className="control-label">Preview Name</label>
            <input
              type="text"
              value={previewName}
              onChange={(e) => setPreviewName(e.target.value)}
              className="control-input"
            />
          </div>

          <div className="control-group">
            <label className="control-label">Preview Icon</label>
            <select
              value={previewIcon}
              onChange={(e) => setPreviewIcon(e.target.value)}
              className="control-input"
            >
              <option value="Heart">Heart</option>
              <option value="Flame">Flame</option>
              <option value="Zap">Zap</option>
              <option value="Rocket">Rocket</option>
              <option value="Cat">Cat</option>
              <option value="Dog">Dog</option>
              <option value="Leaf">Leaf</option>
              <option value="Moon">Moon</option>
            </select>
          </div>
          
          <div className="control-section-title">Card Styling</div>

          <PhotoshopGradientEditor
            label="Background"
            value={backgroundColor}
            onChange={setBackgroundColor}
            key={`bg-${selectedDesign?.id || 'new'}`}
          />

          <PhotoshopGradientEditor
            label="Border"
            value={borderColor}
            onChange={setBorderColor}
            key={`border-${selectedDesign?.id || 'new'}`}
          />

          <div className="control-section-title">Background Settings</div>

          <div className="control-group">
            <label className="control-label">
              Background Opacity: {bgOpacity}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={bgOpacity}
              onChange={(e) => setBgOpacity(parseInt(e.target.value))}
              className="control-slider"
            />
          </div>

          <div className="control-group">
            <label className="control-label">Background Image</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                
                const validation = validateFile(file, {
                  allowedTypes: ['image/png', 'image/jpeg', 'image/gif'],
                  allowedExtensions: ['.png', '.jpg', '.jpeg', '.gif']
                });
                
                if (!validation.valid) {
                  setBgImageUploadStatus({ type: 'error', message: validation.error });
                  return;
                }
                
                try {
                  setBgImageUploadStatus({ type: 'loading', message: 'Uploading...' });
                  const url = await uploadToBlob(file, 'backgrounds');
                  setBgImage(url);
                  setBgImageUploadStatus({ type: 'success', message: 'Uploaded!' });
                  setTimeout(() => setBgImageUploadStatus(null), 2000);
                } catch (error) {
                  setBgImageUploadStatus({ type: 'error', message: error.message });
                }
              }}
              className="control-input"
            />
            {bgImageUploadStatus && (
              <span className={`upload-status upload-status--${bgImageUploadStatus.type}`}>
                {bgImageUploadStatus.message}
              </span>
            )}
            {bgImage && (
              <button
                onClick={() => setBgImage('')}
                className="btn-secondary btn-small"
                style={{ marginTop: '8px' }}
              >
                Remove Image
              </button>
            )}
          </div>

          <div className="control-group">
            <label className="control-label">
              Image Opacity: {bgImageOpacity}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={bgImageOpacity}
              onChange={(e) => setBgImageOpacity(parseInt(e.target.value))}
              className="control-slider"
              disabled={!bgImage}
            />
            <div className="control-note">Blend the image with the gradient underneath.</div>
          </div>

          <div className="control-group">
            <label className="control-checkbox">
              <input
                type="checkbox"
                checked={backdropBlurEnabled}
                onChange={(e) => setBackdropBlurEnabled(e.target.checked)}
              />
              <span>Frosted Glass Effect (Backdrop Blur)</span>
            </label>
          </div>

          {backdropBlurEnabled && (
            <div className="control-group control-group--indented">
              <label className="control-label">
                Blur Amount: {backdropBlurAmount}px
              </label>
              <input
                type="range"
                min="0"
                max="30"
                value={backdropBlurAmount}
                onChange={(e) => setBackdropBlurAmount(parseInt(e.target.value))}
                className="control-slider"
              />
            </div>
          )}

          <div className="control-section-title">Particle Effects (particles.js)</div>

          <div className="control-group">
            <label className="control-label">Preset</label>
            <select
              value={particlePreset}
              onChange={(e) => {
                setParticlePreset(e.target.value);
                setParticleConfigError(null);
              }}
              className="control-input"
            >
              {Object.entries(CARD_PARTICLE_PRESETS).map(([key, preset]) => (
                <option key={key} value={key}>{preset.label}</option>
              ))}
              <option value="custom">Custom JSON</option>
            </select>
            <div className="control-note">Presets use particles.js configs and run on all cards that enable them.</div>
          </div>
          
          <div className="control-group">
            <label className="control-label">
              Particle Size Scale: {particleSizeScale.toFixed(2)}
            </label>
            <input
              type="range"
              min="0.05"
              max="3"
              step="0.05"
              value={particleSizeScale}
              onChange={(e) => setParticleSizeScale(parseFloat(e.target.value))}
              className="control-slider"
            />
            <div className="control-note">Use lower values for ultra-fine particles.</div>
          </div>

          {particlePreset === 'custom' && (
            <div className="control-group">
              <label className="control-label">Custom particles.js JSON</label>
              <textarea
                value={particleConfigText}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setParticleConfigText(nextValue);
                  if (!nextValue.trim()) {
                    setParticleConfig(null);
                    setParticleConfigError('Paste a valid JSON config.');
                    return;
                  }
                  try {
                    const parsed = JSON.parse(nextValue);
                    setParticleConfig(parsed);
                    setParticleConfigError(null);
                  } catch (err) {
                    setParticleConfig(null);
                    setParticleConfigError('Invalid JSON.');
                  }
                }}
                placeholder='Paste particles.js config JSON here...'
                className="control-input control-textarea"
                rows={8}
              />
              {particleConfigError && (
                <div className="control-note control-note--error">{particleConfigError}</div>
              )}
            </div>
          )}

          <div className="control-section-title">Card Glow Effects</div>

          <div className="control-group">
            <label className="control-checkbox">
              <input
                type="checkbox"
                checked={cardOuterGlowEnabled}
                onChange={(e) => setCardOuterGlowEnabled(e.target.checked)}
              />
              <span>Outer Glow</span>
            </label>
          </div>

          {cardOuterGlowEnabled && (
            <>
              <ColorPickerInput
                label="Glow Color"
                value={cardOuterGlowColor}
                onChange={setCardOuterGlowColor}
                className="control-group--indented"
              />
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Size: {cardOuterGlowSize}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={cardOuterGlowSize}
                  onChange={(e) => setCardOuterGlowSize(parseInt(e.target.value))}
                  className="control-slider"
                />
              </div>
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Opacity: {Math.round(cardOuterGlowOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={cardOuterGlowOpacity}
                  onChange={(e) => setCardOuterGlowOpacity(parseFloat(e.target.value))}
                  className="control-slider"
                />
              </div>
            </>
          )}

          <div className="control-group">
            <label className="control-checkbox">
              <input
                type="checkbox"
                checked={cardInnerGlowEnabled}
                onChange={(e) => setCardInnerGlowEnabled(e.target.checked)}
              />
              <span>Inner Glow</span>
            </label>
          </div>

          {cardInnerGlowEnabled && (
            <>
              <ColorPickerInput
                label="Glow Color"
                value={cardInnerGlowColor}
                onChange={setCardInnerGlowColor}
                className="control-group--indented"
              />
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Size: {cardInnerGlowSize}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={cardInnerGlowSize}
                  onChange={(e) => setCardInnerGlowSize(parseInt(e.target.value))}
                  className="control-slider"
                />
              </div>
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Opacity: {Math.round(cardInnerGlowOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={cardInnerGlowOpacity}
                  onChange={(e) => setCardInnerGlowOpacity(parseFloat(e.target.value))}
                  className="control-slider"
                />
              </div>
            </>
          )}

          <div className="control-section-title">Text Styling</div>

          <ColorPickerInput
            label="Text Color"
            value={textColor}
            onChange={setTextColor}
          />

          <div className="control-group">
            <label className="control-checkbox">
              <input
                type="checkbox"
                checked={textGlowEnabled}
                onChange={(e) => setTextGlowEnabled(e.target.checked)}
              />
              <span>Text Glow</span>
            </label>
          </div>

          {textGlowEnabled && (
            <>
              <ColorPickerInput
                label="Glow Color"
                value={textGlowColor}
                onChange={setTextGlowColor}
                className="control-group--indented"
              />
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Size: {textGlowSize}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={textGlowSize}
                  onChange={(e) => setTextGlowSize(parseInt(e.target.value))}
                  className="control-slider"
                />
              </div>
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Opacity: {Math.round(textGlowOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={textGlowOpacity}
                  onChange={(e) => setTextGlowOpacity(parseFloat(e.target.value))}
                  className="control-slider"
                />
              </div>
            </>
          )}

          <div className="control-section-title">Icon Styling</div>

          <ColorPickerInput
            label="Icon Color"
            value={iconColor}
            onChange={setIconColor}
          />

          <div className="control-group">
            <label className="control-checkbox">
              <input
                type="checkbox"
                checked={iconGlowEnabled}
                onChange={(e) => setIconGlowEnabled(e.target.checked)}
              />
              <span>Icon Glow</span>
            </label>
          </div>

          {iconGlowEnabled && (
            <>
              <ColorPickerInput
                label="Glow Color"
                value={iconGlowColor}
                onChange={setIconGlowColor}
                className="control-group--indented"
              />
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Size: {iconGlowSize}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={iconGlowSize}
                  onChange={(e) => setIconGlowSize(parseInt(e.target.value))}
                  className="control-slider"
                />
              </div>
              <div className="control-group control-group--indented">
                <label className="control-label">
                  Glow Opacity: {Math.round(iconGlowOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={iconGlowOpacity}
                  onChange={(e) => setIconGlowOpacity(parseFloat(e.target.value))}
                  className="control-slider"
                />
              </div>
            </>
          )}

          <div className="studio-save-bar">
            <button
              onClick={handleSave}
              className="button buttonPrimary studio-button"
            >
              <Save size={16} />
              Overwrite
            </button>
            <button
              onClick={handleSaveAs}
              className="button buttonSecondary studio-button"
            >
              <Save size={16} />
              Save As New
            </button>
          </div>
          </div>
        </div>

        {/* Right Panel - Saved Designs */}
        <div className="studio-panel studio-panel--library">
          <h2 className="panel-title">Saved Designs</h2>
          {loading ? (
            <div className="designs-loading">
              <Loader2 size={20} className="spin" />
              Loading...
            </div>
          ) : cardDesigns.length === 0 ? (
            <div className="designs-empty">No designs yet</div>
          ) : (
            <div className="designs-items">
              {designsByCategory.map((category) => (
                category.items.length > 0 ? (
                  <div key={category.id} className="design-category">
                    <div className="design-category-title">{category.label}</div>
                    <div className="designs-grid">
                      {category.items.map((design) => (
                        <div key={design.id} className="design-card">
                          <button
                            type="button"
                            onClick={() => handleLoadDesign(design)}
                            className="design-preview"
                            title="Load design"
                          >
                            <div className="design-preview-card player-card-custom" style={getDesignPreviewStyles(design)}>
                              <div className="player-card-background" aria-hidden="true" />
                              <div className="player-card-glow" aria-hidden="true" />
                              <div className="player-card-content design-preview-content">
                                <span className="design-preview-text">{design.name}</span>
                                {IconPreview && (
                                  <IconPreview
                                    size={14}
                                    strokeWidth={2.5}
                                    className="design-preview-icon"
                                    style={{
                                      color: 'var(--design-icon-color, #94a3b8)',
                                      filter: 'var(--design-icon-filter, none)'
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          </button>
                          <div className="design-card-actions">
                            <button
                              onClick={() => handleLoadDesign(design)}
                              className="design-action-button"
                              title="Load"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => handleDuplicate(design)}
                              className="design-action-button"
                              title="Duplicate"
                              aria-label="Duplicate"
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              onClick={() => handleToggleLock(design)}
                              className="design-action-button"
                              title={getDesignAccessActionTitle(design, category)}
                              aria-label={getDesignAccessActionTitle(design, category)}
                            >
                              {design.locked ? <Unlock size={14} /> : <Lock size={14} />}
                            </button>
                            <span className="design-meta-pill" title="Access">
                              {getDesignAccessLabel(design, category)}
                            </span>
                            <span className="design-meta-pill" title="Unlock level">
                              {Number.isFinite(Number(design.unlockLevel)) && Number(design.unlockLevel) > 0
                                ? `Lvl ${Number(design.unlockLevel)}`
                                : 'Never'}
                            </span>
                            <button
                              onClick={() => handleDelete(design.id)}
                              className="design-action-button design-action-button--danger"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              ))}
              {uncategorizedDesigns.length > 0 && (
                <div className="design-category">
                  <div className="design-category-title">Uncategorized</div>
                  <div className="designs-grid">
                    {uncategorizedDesigns.map((design) => (
                      <div key={design.id} className="design-card">
                        <button
                          type="button"
                          onClick={() => handleLoadDesign(design)}
                          className="design-preview"
                          title="Load design"
                        >
                          <div className="design-preview-card player-card-custom" style={getDesignPreviewStyles(design)}>
                            <div className="player-card-background" aria-hidden="true" />
                            <div className="player-card-glow" aria-hidden="true" />
                            <div className="player-card-content design-preview-content">
                              <span className="design-preview-text">{design.name}</span>
                              {IconPreview && (
                                <IconPreview
                                  size={14}
                                  strokeWidth={2.5}
                                  className="design-preview-icon"
                                  style={{
                                    color: 'var(--design-icon-color, #94a3b8)',
                                    filter: 'var(--design-icon-filter, none)'
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </button>
                        <div className="design-card-actions">
                          <button
                            onClick={() => handleLoadDesign(design)}
                            className="design-action-button"
                            title="Load"
                          >
                            Load
                          </button>
                          <button
                            onClick={() => handleDuplicate(design)}
                            className="design-action-button"
                            title="Duplicate"
                            aria-label="Duplicate"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => handleToggleLock(design)}
                            className="design-action-button"
                            title={getDesignAccessActionTitle(design)}
                            aria-label={getDesignAccessActionTitle(design)}
                          >
                            {design.locked ? <Unlock size={14} /> : <Lock size={14} />}
                          </button>
                          <span className="design-meta-pill" title="Access">
                            {getDesignAccessLabel(design)}
                          </span>
                          <span className="design-meta-pill" title="Unlock level">
                            {Number.isFinite(Number(design.unlockLevel)) && Number(design.unlockLevel) > 0
                              ? `Lvl ${Number(design.unlockLevel)}`
                              : 'Never'}
                          </span>
                          <button
                            onClick={() => handleDelete(design.id)}
                            className="design-action-button design-action-button--danger"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .studio-tabs {
          display: flex;
          gap: 8px;
          padding: 16px 0 0 0;
          border-bottom: 1px solid var(--lobby-border, rgba(147, 51, 234, 0.2));
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(6px);
          border-radius: 5px;
          padding: 12px 16px;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .studio-tabs {
            top: 0;
            z-index: 50;
            border-radius: 0;
            padding-top: 0;
            padding-bottom: 0;
          }
        }

        /* Mobile: hide sticky, use .studio-tabs-mobile instead */
        @media (max-width: 768px) {
          .studio-tabs {
            display: none;
          }
          .studio-tabs-mobile {
            display: flex;
            gap: 6px;
            padding: 10px 12px;
            background: rgba(15, 23, 42, 0.6);
            border-radius: 5px;
            margin-bottom: 8px;
            border-bottom: 1px solid var(--lobby-border, rgba(147, 51, 234, 0.2));
            position: static;
            z-index: 1;
          }
        }
        .studio-toast--success {
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.5);
          color: #86efac;
        }

        .studio-toast--error {
          background: rgba(220, 38, 38, 0.2);
          border: 1px solid rgba(220, 38, 38, 0.5);
          color: #fca5a5;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .studio-content {
          display: grid;
          grid-template-columns: 400px 1fr;
          align-items: start;
          gap: 24px;
          flex: 1;
          overflow: visible;
          padding: 20px 0 0 0;
          min-height: 0;
          min-width: 0;
        }

        .studio-left {
          display: flex;
          flex-direction: column;
          gap: 24px;
          min-width: 0;
        }
        
        @media (max-width: 768px) {
          .studio-content {
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 16px 0;
          }
        }

        .studio-preview-sticky {
          position: sticky;
          top: 16px;
          z-index: 15;
        }

        .studio-panel {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 5px;
          padding: 20px;
          height: fit-content;
          min-width: 0;
          border: 1px solid var(--lobby-border, rgba(147, 51, 234, 0.2));
          backdrop-filter: blur(6px);
        }
        
        @media (max-width: 768px) {
          .studio-panel {
            padding: 16px;
          }
        }

        .studio-panel--controls {
          /* No independent scrolling - use page scroll */
          overflow-y: visible;
          overflow-x: visible;
        }

        .studio-panel--library {
        }

        .studio-preview-card {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 5px;
          --card-border-radius: 5px;
          position: relative;
          overflow: visible;
          isolation: isolate;
        }

        .studio-preview-content {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
        }

        .studio-preview-name {
          font-size: 14px;
          font-weight: 600;
        }

        .studio-save-bar {
          display: flex;
          gap: 12px;
          margin-top: 28px;
          padding: 16px;
          background: rgba(15, 23, 42, 0.7);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 5px;
          position: sticky;
          bottom: 16px;
          z-index: 10;
          backdrop-filter: blur(8px);
        }

        .studio-save-bar .studio-button {
          margin-bottom: 0;
          flex: 1;
        }
        
        @media (max-width: 768px) {
          .studio-panel--controls {
            /* No scroll container blocking footer */
            overflow-y: visible;
          }

          .studio-preview-sticky {
            top: 16px;
          }
        }

        .panel-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 24px 0;
          color: #e2e8f0;
        }

        .control-group {
          margin-bottom: 20px;
        }

        .control-group--indented {
          margin-left: 20px;
          margin-bottom: 16px;
        }

        .control-section-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--lobby-accent, #60a5fa);
          margin: 24px 0 12px 0;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(147, 51, 234, 0.2);
        }

        .control-label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
          font-weight: 500;
          color: #cbd5e1;
          margin-bottom: 8px;
        }

        .control-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #cbd5e1;
          cursor: pointer;
        }

        .control-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .control-value {
          font-family: monospace;
          font-size: 11px;
          color: #94a3b8;
          max-width: 55%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .control-note {
          font-size: 11px;
          color: #64748b;
          margin-top: 4px;
          font-style: italic;
        }

        .control-note--error {
          color: #fca5a5;
        }

        .control-input {
          width: 100%;
          padding: 10px 12px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          color: #e2e8f0;
          font-size: 14px;
          outline: none;
        }

        .control-input:focus {
          border-color: #3b82f6;
        }

        .category-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .category-button-group {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.35);
          border: 1px solid rgba(71, 85, 105, 0.35);
        }

        .category-button {
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid rgba(71, 85, 105, 0.6);
          background: rgba(15, 23, 42, 0.5);
          color: #cbd5e1;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .category-button:hover {
          border-color: rgba(59, 130, 246, 0.7);
          color: #e2e8f0;
        }

        .category-button--active {
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.7);
          color: #e2e8f0;
        }

        .category-button--add {
          border-style: dashed;
          color: #94a3b8;
        }

        .category-button-actions {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding-right: 4px;
        }

        .category-action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 999px;
          border: 1px solid rgba(71, 85, 105, 0.6);
          background: rgba(15, 23, 42, 0.5);
          color: #cbd5e1;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .category-action:hover {
          border-color: rgba(59, 130, 246, 0.7);
          color: #e2e8f0;
        }

        .category-action--active {
          border-color: rgba(251, 191, 36, 0.75);
          color: #fcd34d;
          background: rgba(120, 53, 15, 0.35);
        }

        .category-action--active:hover {
          border-color: rgba(251, 191, 36, 0.9);
          color: #fde68a;
        }

        .category-action--danger:hover {
          border-color: rgba(239, 68, 68, 0.8);
          color: #fecaca;
        }

        .control-textarea {
          min-height: 160px;
          resize: vertical;
          font-family: monospace;
          line-height: 1.4;
        }

        .control-slider {
          width: 100%;
          height: 6px;
          background: rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          outline: none;
          cursor: pointer;
        }

        .studio-button {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .studio-button-row {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .designs-utility {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .design-category {
          margin-bottom: 16px;
        }

        .design-category-title {
          font-size: 12px;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .designs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 12px;
        }

        .design-card {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 10px;
          border-radius: 5px;
          background: rgba(15, 23, 42, 0.4);
          border: 1px solid rgba(71, 85, 105, 0.3);
        }

        .design-preview {
          background: transparent;
          border: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
        }

        .design-preview-card {
          width: 100%;
          aspect-ratio: 3 / 1;
          border-radius: 5px;
          --card-border-radius: 5px;
          position: relative;
          overflow: visible;
          isolation: isolate;
        }

        .design-preview-content {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: var(--design-text-color, #e2e8f0);
          text-shadow: var(--design-text-shadow, none);
          mix-blend-mode: screen;
          font-size: 12px;
          font-weight: 600;
          z-index: 3;
        }

        .design-preview-icon {
          color: var(--design-icon-color, #94a3b8);
          filter: var(--design-icon-filter, none);
        }

        .design-preview-name {
          font-size: 12px;
          font-weight: 600;
          color: #e2e8f0;
          text-align: center;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .design-card-actions {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .design-meta-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px 10px;
          border-radius: 5px;
          border: 1px solid rgba(71, 85, 105, 0.5);
          background: rgba(15, 23, 42, 0.45);
          color: #cbd5e1;
          font-size: 11px;
          line-height: 1;
        }

        .design-card-actions .design-action-button {
          flex: 1 1 80px;
        }

        .designs-loading,
        .designs-empty {
          padding: 20px;
          text-align: center;
          color: #94a3b8;
          font-size: 14px;
        }

        .designs-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .designs-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .design-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.3);
          border-radius: 5px;
        }

        .design-item-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .design-item-name {
          font-size: 14px;
          color: #e2e8f0;
        }

        .design-item-lock {
          color: #94a3b8;
        }

        .design-item-actions {
          display: flex;
          gap: 4px;
        }

        .design-action-button {
          padding: 6px 12px;
          background: rgba(51, 65, 85, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          color: #cbd5e1;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .design-action-button:hover {
          background: rgba(71, 85, 105, 0.7);
          border-color: rgba(100, 116, 139, 0.7);
        }

        .design-action-button--danger:hover {
          background: rgba(220, 38, 38, 0.2);
          border-color: rgba(220, 38, 38, 0.5);
          color: #fca5a5;
        }

        @media (max-width: 1024px) {
          .studio-content {
            grid-template-columns: 1fr;
          }

          .studio-panel--controls {
            max-height: none;
          }
        }
      `}</style>
    </>
  );
}

// Custom Icons Tab Component
function CustomIconsTab() {
  const { customIcons, saveCustomIcon, deleteCustomIcon, toggleIconLock, loading } = useCustomIcons();
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('regular');

  const resolveIconCategory = (icon) => {
    if (icon.category) return icon.category;
    if (icon.type === 'png' || icon.type === 'gif') return 'full-color';
    if (icon.type === 'lottie') return 'animated';
    return 'regular';
  };

  const iconCategoryOptions = [
    { id: 'regular', label: 'Regular' },
    { id: 'full-color', label: 'Full Color' },
    { id: 'animated', label: 'Animated' }
  ];
  const iconSections = iconCategoryOptions;
  const iconsByCategory = iconSections.map((section) => ({
    ...section,
    items: customIcons.filter((icon) => resolveIconCategory(icon) === section.id)
  }));

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validateFile(file, {
      allowedTypes: ['image/svg+xml', 'image/png', 'image/gif', 'application/json'],
      allowedExtensions: ['.svg', '.png', '.gif', '.json']
    });
    
    if (!validation.valid) {
      setUploadStatus({ type: 'error', message: validation.error });
      return;
    }

    try {
      setUploadStatus({ type: 'info', message: 'Uploading...' });

      // Upload to Vercel Blob
      const url = await uploadToBlob(file, 'icons');

      // Save icon data to Firestore
      const iconId = file.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
      const iconData = {
        id: iconId,
        name: file.name.replace(/\.[^/.]+$/, ''),
        type: file.type.includes('svg') ? 'svg' : file.type.includes('png') ? 'png' : file.type.includes('gif') ? 'gif' : 'lottie',
        url: url,
        category: uploadCategory,
        locked: false,
        createdAt: new Date()
      };
      
      const success = await saveCustomIcon(iconId, iconData);
      
      if (success) {
        setUploadStatus({ type: 'success', message: 'Icon uploaded successfully!' });
        setTimeout(() => setUploadStatus(null), 3000);
      } else {
        setUploadStatus({ type: 'error', message: 'Failed to save icon to database' });
      }

    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus({ type: 'error', message: `Upload failed: ${err.message}` });
    }
  };

  const handleToggleLock = async (icon) => {
    await toggleIconLock(icon.id, icon.locked);
  };

  const handleDelete = async (iconId) => {
    if (!confirm('Are you sure you want to delete this icon?')) return;
    const success = await deleteCustomIcon(iconId);
    if (success) {
      setUploadStatus({ type: 'success', message: 'Icon deleted successfully!' });
      setTimeout(() => setUploadStatus(null), 3000);
    }
  };

  return (
    <>
      {uploadStatus && (
        <div className={`studio-toast studio-toast--${uploadStatus.type}`}>
          {uploadStatus.message}
        </div>
      )}

      <div className="icons-content">
        <div className="icons-upload-section">
          <h2 className="section-title">Upload Custom Icon</h2>
          <p className="section-description">
            Upload SVG, PNG, GIF, or Lottie JSON files. Icons will be available for players once unlocked.
          </p>
          <div className="control-group">
            <label className="control-label">Category</label>
            <div className="category-buttons">
              {iconCategoryOptions.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`category-button ${uploadCategory === category.id ? 'category-button--active' : ''}`}
                  onClick={() => setUploadCategory(category.id)}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
          
          <label className="upload-button">
            <Upload size={20} />
            Choose File (SVG, PNG, GIF, Lottie)
            <input
              type="file"
              accept=".svg,.png,.gif,.json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>

          <div className="upload-note">
            <strong>Vercel Blob Storage:</strong> Icons are uploaded to Vercel Blob and URLs stored in Firestore. 
            Maximum file size: 5MB per file.
          </div>
        </div>

        <div className="icons-list-section">
          <h2 className="section-title">Custom Icons</h2>
          
          {loading ? (
            <div className="icons-loading">
              <Loader2 size={20} className="spin" />
              Loading icons...
            </div>
          ) : customIcons.length === 0 ? (
            <div className="icons-empty">
              No custom icons yet. Upload your first icon above!
            </div>
          ) : (
            <div className="icons-sections">
              {iconsByCategory.map((section) => (
                <div key={section.id} className="icons-section">
                  <h3 className="icons-section-title">{section.label}</h3>
                  {section.items.length === 0 ? (
                    <div className="icons-empty">No {section.label.toLowerCase()} icons yet.</div>
                  ) : (
                    <div className="icons-grid">
                      {section.items.map((icon) => (
                        <div key={icon.id} className="icon-card">
                          <div className="icon-card-preview">
                            {renderCustomIcon(icon, 48)}
                          </div>
                          <div className="icon-card-info">
                            <span className="icon-card-name">{icon.name}</span>
                            <span className="icon-card-type">{resolveIconCategory(icon).replace('-', ' ')}</span>
                          </div>
                          <div className="icon-card-actions">
                            <button
                              onClick={() => handleToggleLock(icon)}
                              className="icon-action-button"
                              title={icon.locked ? 'Unlock' : 'Lock'}
                            >
                              {icon.locked ? <Unlock size={14} /> : <Lock size={14} />}
                            </button>
                            <button
                              onClick={() => handleDelete(icon.id)}
                              className="icon-action-button icon-action-button--danger"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .studio-toast {
          position: fixed;
          top: 24px;
          right: 24px;
          padding: 12px 20px;
          border-radius: 5px;
          font-size: 14px;
          z-index: 1000;
          animation: slideInRight 0.3s ease-out;
        }

        .studio-toast--success {
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.5);
          color: #86efac;
        }

        .studio-toast--error {
          background: rgba(220, 38, 38, 0.2);
          border: 1px solid rgba(220, 38, 38, 0.5);
          color: #fca5a5;
        }

        .studio-toast--info {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.5);
          color: #93c5fd;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .icons-content {
          display: grid;
          gap: 24px;
        }

        .icons-upload-section,
        .icons-list-section {
          background: var(--glassy-card-bg);
          backdrop-filter: blur(var(--glassy-card-blur));
          -webkit-backdrop-filter: blur(var(--glassy-card-blur));
          border-radius: 5px;
          padding: 24px;
        }

        .control-group {
          margin-bottom: 16px;
        }

        .control-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #cbd5e1;
          margin-bottom: 8px;
        }

        .control-input {
          width: 100%;
          padding: 10px 12px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          color: #e2e8f0;
          font-size: 14px;
          outline: none;
        }

        .control-input:focus {
          border-color: #3b82f6;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 12px 0;
          color: #e2e8f0;
        }

        .section-description {
          margin: 0 0 20px 0;
          color: #94a3b8;
          font-size: 14px;
          line-height: 1.5;
        }

        .upload-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: #3b82f6;
          color: white;
          border-radius: 5px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .upload-button:hover {
          background: #2563eb;
        }

        .upload-note {
          margin-top: 16px;
          padding: 12px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 5px;
          font-size: 13px;
          color: #93c5fd;
          line-height: 1.5;
        }

        .icons-loading,
        .icons-empty {
          padding: 40px;
          text-align: center;
          color: #94a3b8;
        }

        .icons-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .icons-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }

        .icons-sections {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .icons-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .icons-section-title {
          margin: 0;
          font-size: 13px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #94a3b8;
        }

        .icon-card {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.3);
          border-radius: 5px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .icon-card-preview {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 80px;
          background: rgba(30, 41, 59, 0.5);
          border-radius: 5px;
        }

        .icon-card-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .icon-card-name {
          font-size: 14px;
          font-weight: 500;
          color: #e2e8f0;
        }

        .icon-card-type {
          font-size: 11px;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .icon-card-actions {
          display: flex;
          gap: 8px;
          padding-top: 8px;
          border-top: 1px solid rgba(71, 85, 105, 0.3);
        }

        .icon-action-button {
          flex: 1;
          padding: 8px;
          background: rgba(51, 65, 85, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          color: #cbd5e1;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .icon-action-button:hover {
          background: rgba(71, 85, 105, 0.7);
        }

        .icon-action-button--danger:hover {
          background: rgba(220, 38, 38, 0.2);
          border-color: rgba(220, 38, 38, 0.5);
          color: #fca5a5;
        }

      `}</style>
    </>
  );
}
